'use client';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function CookiePolicyPage() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <section className="bg-primary/10 py-12">
        <div className="container mx-auto px-4">
          <Link href="/">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-4">Cookie Policy</h1>
          <p className="text-xl text-muted-foreground max-w-3xl">
            Kawkb ("we", "our", or "us") uses cookies and similar tracking technologies to enhance
            your experience on our website and improve our services. This Cookie Policy explains
            what cookies are, how we use them, your choices regarding cookies, and further
            information.
          </p>
        </div>
      </section>

      {/* Cookie Policy Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold mb-4">What Are Cookies?</h2>
            <p>
              Cookies are small text files placed on your device by websites you visit. They are
              widely used to make websites work efficiently and to provide information to the site
              owners. Cookies can be "persistent" or "session" cookies.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">How We Use Cookies</h2>
            <p>We use cookies for the following purposes:</p>

            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>
                <strong>Essential Cookies:</strong> These cookies are necessary to provide you with
                services available through our website and to use some of its features. Without
                these cookies, the services you have asked for cannot be provided.
              </li>
              <li>
                <strong>Performance and Analytics Cookies:</strong> These cookies are used to
                collect information about how you interact with our website. We use the information
                to improve and optimize our website performance.
              </li>
              <li>
                <strong>Functionality Cookies:</strong> These cookies allow us to remember your
                choices and preferences, providing you with a more personalized experience.
              </li>
              <li>
                <strong>Advertising and Targeting Cookies:</strong> These cookies are used to
                deliver advertisements that are relevant to you. They help us track the
                effectiveness of our advertising campaigns.
              </li>
            </ul>

            <h2 className="text-2xl font-bold mb-4 mt-8">Third-Party Cookies</h2>
            <p>
              We may also use cookies provided by trusted third parties to improve our services and
              marketing strategies. These third-party services may include analytics providers,
              social media platforms, and advertising networks.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Your Cookie Choices</h2>
            <p>
              You can control and manage cookies through your browser settings. You can also manage
              your preferences through the cookie consent banner that appears on your first visit to
              our website.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Changes to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Please revisit this page
              periodically to stay informed about our use of cookies.
            </p>

            <h2 className="text-2xl font-bold mb-4 mt-8">Contact Us</h2>
            <p>
              If you have any questions or concerns regarding this Cookie Policy, please contact us
              at{' '}
              <a href="mailto:support@kawkb.com" className="text-blue-600 hover:underline">
                support@kawkb.com
              </a>
              .
            </p>

            <p className="mt-6 font-medium">
              By using our website, you consent to the use of cookies as described in this policy.
            </p>

            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-muted-foreground text-sm">
                Last updated:{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
